/**
 * The lead desk, inside Manifest Review (Isaiah Cotto, 2026-09-30), as boards
 * (2026-10-07): the same table, cells and docked record as Scheduling, Finance
 * and the UMED board.
 *
 * "No automatic distribution; that should live in the same view for Gwynn,
 * April, Dominique and Kaleigh. Because each PA has facilities they are
 * assigned to ... so leads aren't getting distributed to the wrong PAs. For
 * example Talhia is Spanish and takes the Spanish-heavy facilities."
 *
 * Two views:
 *   Leads to place     one row per RESIDENT nobody holds -- new CounselEAR
 *                      TWL + Qualified, or recorded in Manifest Review -- with
 *                      the PA who covers the facility beside it. Placing moves
 *                      every open copy of the resident together. The row opens
 *                      DOCKED beside the table: Place, This building (place
 *                      every resident at it with the covering PA), Close
 *                      instead (Disposition, R5) and Details. Nothing grows
 *                      inside the table.
 *   Facility coverage  which PA covers each building and why. Rows seeded
 *                      from history say so until somebody confirms them; the
 *                      edit is in the docked record.
 *
 * Default order is building A to Z, then resident A to Z (April Torino,
 * 2026-09-30: "it would be best if leads are alphabetized by facility name
 * first ... what we typically do is assign all leads from one facility at
 * once").
 *
 * Select-and-send-back (D-28, Dominique Genua 2026-10-02): a placer ticks leads
 * (a row, a building from its record, or everything shown) and sends the set
 * back to the provider, or closes it another way, from the board's head. A
 * lead a VA verified as qualifying (Needs verification, D-29) returns here,
 * tagged "Verified — qualifies", with who checked it and what they found.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { manifestApi, reasonWords, type Advocate, type Coverage, type Lead } from './manifestApi';
import { DispositionAction } from './ResubmittalsDesk';
import BulkDisposition from './BulkDisposition';
import { MfLoadFailure, MfSkeleton } from './mfStates';
import { StateBlock as TideState } from '../../components/tide';
import { verificationApi, type VerifiedLead } from './verificationApi';
import MfBoard, { useClientSort, useOpenParam, type BoardColumn, type BoardDetailSection } from './board/MfBoard';
import MfLegend, { type MfLegendItem } from './board/MfLegend';
import { ResidentName, residentLabel } from './board/residents';
import { ToneChip } from './board/chips';

const longDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' }) : '—');
/** A lead, plus what a VA found when this resident came back from Needs verification. */
type DeskLead = Lead & { verified?: VerifiedLead };
const byName = (a: string, b: string) => a.localeCompare(b, 'en-US', { sensitivity: 'base' });

/** Not a new lead: another episode of the resident (already sold, UMED in flight, ...). */
const notNew = (l: Lead) => Boolean(l.episode && l.episode.verdict !== 'NEW_LEAD_OK');
const placeable = (l: Lead) => l.workable !== false && !notNew(l);
const finishedWords = (l: Lead) => `Only a finished case on file (${(l.finished_disposition || l.finished_stage || 'finished').replace(/_/g, ' ')}${l.finished_opened_on ? `, opened ${new Date(l.finished_opened_on).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })}` : ''}). Placing it would give the PA nothing to work. Record the resident in Manifest Review to open a new lead.`;
const holdsOf = (l: Lead) => (l.intake_exceptions || []).filter(Boolean);

/** Legend keys: where a lead came from, and whether it can be placed. */
type LeadKey = 'src:verified' | 'src:reopened' | 'src:counselear' | 'src:recorded' | 'can:ready' | 'can:notnew' | 'can:finished';
const hasKey = (l: DeskLead, k: string): boolean => {
  switch (k) {
    case 'src:verified': return Boolean(l.verified);
    case 'src:reopened': return Boolean(l.reopened_tag);
    case 'src:counselear': return Boolean(l.counselear_lead);
    case 'src:recorded': return Boolean(l.recorded);
    case 'can:finished': return l.workable === false;
    case 'can:notnew': return l.workable !== false && notNew(l);
    case 'can:ready': return placeable(l);
    default: return false;
  }
};

const SourceChip = ({ k, l }: { k: LeadKey; l?: DeskLead }) => {
  switch (k) {
    case 'src:verified': return <ToneChip tone="ok">Verified — qualifies</ToneChip>;
    case 'src:reopened': return <ToneChip tone="attn">{l?.reopened_tag || 'Reopened'}</ToneChip>;
    case 'src:counselear': return <ToneChip tone="ok">CounselEAR TWL + Qualified</ToneChip>;
    case 'src:recorded': return <ToneChip tone="quiet">Recorded in Manifest Review</ToneChip>;
    case 'can:ready': return <ToneChip tone="ok">Ready</ToneChip>;
    case 'can:notnew': return <ToneChip tone="attn">Not a new lead</ToneChip>;
    default: return <ToneChip tone="attn">Finished case only</ToneChip>;
  }
};

const SOURCE_KEYS: LeadKey[] = ['src:verified', 'src:reopened', 'src:counselear', 'src:recorded'];
const CAN_KEYS: LeadKey[] = ['can:ready', 'can:notnew', 'can:finished'];
const LEAD_MEANING: Record<LeadKey, string> = {
  'src:verified': 'A VA checked this resident in Needs verification and found they qualify; the lead returned here.',
  'src:reopened': 'A manager reopened a closed lead with nobody to hold it; the tag says why.',
  'src:counselear': 'CounselEAR carries the TWL tag and the resident qualifies.',
  'src:recorded': 'Recorded in Manifest Review (a TWL day), not read from CounselEAR.',
  'can:ready': 'A new lead with a case to work: it can be placed with a PA.',
  'can:notnew': 'Another episode of this resident exists (already sold, or UMED in flight). Not placed from here.',
  'can:finished': 'Only a finished case is on file (DNQ, NITWL, enrolled). Placing it would give the PA nothing to work.',
};
const LEAD_LABEL: Record<LeadKey, string> = {
  'src:verified': 'Verified — qualifies', 'src:reopened': 'Reopened', 'src:counselear': 'CounselEAR TWL + Qualified', 'src:recorded': 'Recorded in Manifest Review',
  'can:ready': 'Ready', 'can:notnew': 'Not a new lead', 'can:finished': 'Finished case only',
};

const LEAD_SORTERS: Record<string, (l: DeskLead) => string | number | null> = {
  name: (l) => [l.first_name, l.last_name].filter(Boolean).join(' ') || null,
  // Building first, then resident (rows arrive name-ordered, so ties keep that order).
  building: (l) => (l.facility_name ? `${l.facility_name}\u0000${[l.first_name, l.last_name].filter(Boolean).join(' ')}` : null),
  twl: (l) => l.twl_tag_since,
  pa: (l) => l.suggested_advocate,
  copies: (l) => l.open_cases,
};

/** Place one resident: choose a PA (the covering one is preselected) and place. Placing moves every open copy together. */
function PlacePanel({ lead, advocates, onPlaced }: { lead: DeskLead; advocates: Advocate[]; onPlaced: (msg: string) => void }) {
  const [choice, setChoice] = useState<string>(lead.suggested_advocate_id || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const chosen = advocates.find((a) => a.id === choice);
  const label = residentLabel(lead);
  const place = async () => {
    if (!choice) return;
    setBusy(true); setErr(null);
    try {
      const r = await manifestApi.place(lead.case_id, choice);
      if (r.success && r.data) onPlaced(`${label} placed with ${chosen ? chosen.name : 'the PA'}${r.data.swept ? ` (and ${r.data.swept} other open ${r.data.swept === 1 ? 'copy' : 'copies'})` : ''}.`);
      else setErr(r.code === 'ASSIGNMENT_CONFLICT' ? 'Someone placed this resident since the list loaded. Refresh to see who.' : (r.message || r.error || 'Could not place this lead.'));
    } catch { setErr('Could not reach eMerge. Try again.'); } finally { setBusy(false); }
  };
  if (!placeable(lead)) {
    return (
      <div className="mf-panel">
        <p>{lead.workable === false
          ? 'This resident cannot be placed: only a finished case is on file.'
          : 'This resident cannot be placed from here: they are not a new lead. If the provider saw them again for a replacement, record it on Replacements.'}</p>
      </div>
    );
  }
  return (
    <div className="mf-panel">
      <p>{lead.open_cases > 1 ? `${lead.open_cases} open copies of this resident: placing moves them together.` : 'Placing gives this resident to the PA you choose.'}</p>
      <div className="mf-acts">
        <select aria-label={`PA for ${label}`} value={choice} onChange={(e) => setChoice(e.target.value)} disabled={busy}>
          <option value="">Choose a PA…</option>
          {advocates.map((a) => <option key={a.id} value={a.id}>{a.name}{a.manager ? ' (manager)' : ''} · {a.open_cases} open</option>)}
        </select>
        <button type="button" className="board-btn board-btn--primary" onClick={place} disabled={!choice || busy}>
          {busy ? 'Placing…' : chosen ? `Place with ${chosen.name.split(' ')[0]}` : 'Place'}
        </button>
      </div>
      {err && <p className="mf-notice mf-notice--stop" role="alert">{err}</p>}
    </div>
  );
}

/**
 * One building's residents: "Place all" places each in turn with the covering
 * PA -- the same placement as the Place button, one audited assignment per
 * resident -- after a confirmation. Also ticks the building for the bulk acts.
 */
function BuildingPanel({ lead, rows, advocates, selected, onSelect, onPlaced }: {
  lead: DeskLead; rows: DeskLead[]; advocates: Advocate[]; selected: Set<string>; onSelect: (ids: string[], on: boolean) => void; onPlaced: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const facility = lead.facility_name || 'Facility not on file';
  const pa = lead.suggested_advocate_id ? advocates.find((a) => a.id === lead.suggested_advocate_id) : undefined;
  const ready = rows.filter(placeable);
  const allHere = rows.length > 0 && rows.every((l) => selected.has(l.case_id));
  const placeAll = async () => {
    if (!pa || !window.confirm(`Place all ${ready.length} ${ready.length === 1 ? 'resident' : 'residents'} at ${facility} with ${pa.name}?`)) return;
    setBusy(true); setErr(null);
    const failed: string[] = [];
    let placed = 0;
    for (const l of ready) {
      try {
        const r = await manifestApi.place(l.case_id, pa.id);
        if (r.success) placed += 1; else failed.push(residentLabel(l));
      } catch { failed.push(residentLabel(l)); }
    }
    setBusy(false);
    if (failed.length) setErr(`Not placed: ${failed.join(', ')}. Refresh and place them one by one.`);
    onPlaced(`${placed} of ${ready.length} at ${facility} placed with ${pa.name}.`);
  };
  return (
    <div className="mf-panel">
      <p>{rows.length} {rows.length === 1 ? 'resident' : 'residents'} at {facility} ·{' '}
        {lead.suggested_advocate ? <>covered by <strong>{lead.suggested_advocate}</strong>{!lead.coverage_confirmed && ' (not yet confirmed)'}</> : 'no PA set'}</p>
      {err && <p className="mf-notice mf-notice--stop" role="alert">{err}</p>}
      <div className="mf-acts">
        {pa && ready.length > 0 && (
          <button type="button" className="board-btn board-btn--primary" onClick={placeAll} disabled={busy}>
            {busy ? 'Placing…' : `Place all ${ready.length} with ${pa.name.split(' ')[0]}`}
          </button>
        )}
        <label className="mf-check">
          <input type="checkbox" checked={allHere} onChange={() => onSelect(rows.map((l) => l.case_id), !allHere)} aria-label={`Select all in this building: ${facility}`} />
          Select all in this building
        </label>
      </div>
    </div>
  );
}

function LeadsBoard() {
  const [leads, setLeads] = useState<DeskLead[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [advocates, setAdvocates] = useState<Advocate[]>([]);
  const [can, setCan] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [legendKey, setLegendKey] = useState<string | null>(null);
  const [open, setOpen] = useOpenParam('open');

  const load = useCallback(async () => {
    setProblem(null);
    try {
      // The verified-and-returned list rides along; if it cannot be read the leads still show.
      const [r, v] = await Promise.all([manifestApi.leads(), verificationApi.list().catch(() => null)]);
      if (r.success && r.data) {
        const returned = v && v.success && v.data && Array.isArray(v.data.returned) ? v.data.returned : [];
        const byPatient = new Map(returned.map((x) => [x.patient_id, x]));
        const seen = new Set(r.data.items.map((l) => l.patient_id));
        const merged: DeskLead[] = r.data.items.map((l) => (byPatient.has(l.patient_id) ? { ...l, verified: byPatient.get(l.patient_id) } : l));
        for (const x of returned) {
          if (seen.has(x.patient_id)) continue;
          merged.push({
            patient_id: x.patient_id, first_name: x.first_name, last_name: x.last_name, facility_id: x.facility_id, facility_name: x.facility_name,
            twl_tag_since: x.twl_tag_since, counselear_lead: false, recorded: false, case_id: x.case_id, intake_exceptions: null, open_cases: x.open_cases,
            suggested_advocate_id: x.suggested_advocate_id, suggested_advocate: x.suggested_advocate, coverage_reason: x.coverage_reason,
            coverage_source: x.coverage_source, coverage_confirmed: x.coverage_confirmed, workable: true, verified: x,
          });
        }
        setLeads(merged); setAdvocates(r.data.advocates); setCan(r.data.canPlace);
        const here = new Set(merged.map((l) => l.case_id));
        setSelected((prev) => new Set(Array.from(prev).filter((id) => here.has(id))));
      } else setProblem(r.message || r.error || 'Could not load the leads.');
    } catch { setProblem('Could not reach eMerge.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Residents A to Z first, so the building sort (and every tie) reads as April asked.
  const all = useMemo(() => (leads || []).slice().sort((a, b) => byName(residentLabel(a), residentLabel(b))), [leads]);
  const found = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? all.filter((l) => `${residentLabel(l)} ${l.facility_name || ''} ${l.suggested_advocate || ''}`.toLowerCase().includes(q)) : all;
  }, [all, filter]);
  const filtered = useMemo(() => (legendKey ? found.filter((l) => hasKey(l, legendKey)) : found), [found, legendKey]);
  const { rows: shown, sort, dir, sorts, onSort } = useClientSort(filtered, LEAD_SORTERS, { sort: 'building' });

  const shownIds = useMemo(() => shown.map((l) => l.case_id), [shown]);
  const hiddenSelected = useMemo(() => { const s = new Set(shownIds); return Array.from(selected).filter((id) => !s.has(id)).length; }, [selected, shownIds]);
  const allShown = shownIds.length > 0 && shownIds.every((id) => selected.has(id));
  const select = useCallback((ids: string[], on: boolean) => setSelected((prev) => {
    const n = new Set(prev);
    for (const id of ids) { if (on) n.add(id); else n.delete(id); }
    return n;
  }), []);
  const labelFor = useCallback((id: string) => { const l = (leads || []).find((x) => x.case_id === id); return l ? residentLabel(l) : undefined; }, [leads]);
  const placed = (m: string) => { setNotice(m); load(); };

  const legendItems: MfLegendItem[] = useMemo(() => {
    const out: MfLegendItem[] = [];
    for (const k of [...SOURCE_KEYS, ...CAN_KEYS]) {
      const hits = found.filter((l) => hasKey(l, k));
      if (!hits.length) continue;
      out.push({ key: k, label: LEAD_LABEL[k], sample: <SourceChip k={k} l={hits[0]} />, meaning: LEAD_MEANING[k], count: hits.length });
    }
    return out;
  }, [found]);

  if (problem) return <main className="mf-main mf-main--board"><MfLoadFailure message={problem} retry={load} /></main>;

  const columns: BoardColumn<DeskLead>[] = [
    ...(can ? [{
      key: 'pick', label: 'Select', help: 'Pick residents to send back or close in one governed action, from the controls above the table.',
      render: (l: DeskLead) => <input type="checkbox" checked={selected.has(l.case_id)} onChange={() => select([l.case_id], !selected.has(l.case_id))} aria-label={`Select ${residentLabel(l)}`} />,
    }] : []),
    { key: 'building', label: 'Building', sort: 'building', help: 'The building the resident is in. Buildings run A to Z, residents A to Z inside each.', render: (l) => (l.facility_name ? <span className="board-clip" title={l.facility_name}>{l.facility_name}</span> : <span className="board-na">not on file</span>) },
    { key: 'twl', label: 'TWL since', sort: 'twl', help: 'The day the TWL tag was put on the resident.', render: (l) => (l.twl_tag_since ? <span>{longDate(l.twl_tag_since)}</span> : <span className="board-na">not recorded</span>) },
    {
      key: 'source', label: 'Source', help: 'Where the lead came from: CounselEAR, Manifest Review, a VA check, or a reopened lead.',
      render: (l) => {
        const keys = SOURCE_KEYS.filter((k) => hasKey(l, k));
        return keys.length ? <span className="mf-chips">{keys.map((k) => <SourceChip key={k} k={k} l={l} />)}</span> : <span className="board-na">not recorded</span>;
      },
    },
    {
      key: 'pa', label: 'Covering PA', sort: 'pa', help: 'The PA who covers this building. A suggestion, never an assignment: the placer can choose anyone.',
      render: (l) => (l.suggested_advocate
        ? <span className="board-clip" title={l.suggested_advocate}>{l.suggested_advocate}{!l.coverage_confirmed && <span className="board-src"> (not confirmed)</span>}</span>
        : <span className="board-na">No PA set</span>),
    },
    { key: 'copies', label: 'Open copies', numeric: true, sort: 'copies', help: 'How many open copies of this resident exist. Placing moves them together.', render: (l) => <span>{l.open_cases}</span> },
    {
      key: 'holds', label: 'Holds', help: 'Why the PA\'s queue holds this resident until something is fixed.',
      render: (l) => { const h = holdsOf(l).map(reasonWords).join('; '); return h ? <span className="board-clip" title={h}>{h}</span> : <span className="board-na">none</span>; },
    },
    { key: 'can', label: 'Can place', help: 'Ready: a new lead with a case to work. Not a new lead: another episode exists. Finished case only: nothing to work.', render: (l) => <SourceChip k={l.workable === false ? 'can:finished' : notNew(l) ? 'can:notnew' : 'can:ready'} /> },
  ];

  const identity = {
    label: 'Resident', sort: 'name',
    help: 'A resident nobody holds. Open the row for the record docked beside the table: place them, place their building, close them instead, or read the details.',
    render: (l: DeskLead) => <ResidentName p={l} />,
    name: (l: DeskLead) => residentLabel(l),
  };

  const sections = (l: DeskLead): BoardDetailSection[] => {
    const holds = holdsOf(l);
    const inBuilding = found.filter((x) => (x.facility_id || 'none') === (l.facility_id || 'none'));
    const out: BoardDetailSection[] = [];
    if (can) {
      out.push({ id: 'place', label: 'Place', render: () => <PlacePanel key={l.case_id} lead={l} advocates={advocates} onPlaced={placed} /> });
      out.push({ id: 'building', label: 'This building', render: () => <BuildingPanel key={l.case_id} lead={l} rows={inBuilding} advocates={advocates} selected={selected} onSelect={select} onPlaced={placed} /> });
      out.push({
        id: 'close', label: 'Close instead',
        render: () => (
          <div className="mf-panel">
            <p>Close this lead with a disposition and a reason instead of placing it with a PA.</p>
            <DispositionAction caseId={l.case_id} residentLabel={residentLabel(l)} onDone={placed} />
          </div>
        ),
      });
    }
    out.push({
      id: 'details', label: 'Details',
      render: () => (
        <div className="mf-panel">
          <dl className="mf-facts">
            <dt>Covering PA</dt>
            <dd>{l.suggested_advocate
              ? <>{l.suggested_advocate}{l.coverage_reason ? ` — ${l.coverage_reason}` : ''}{!l.coverage_confirmed && ' (suggested from history, not yet confirmed)'}</>
              : 'No PA set for this facility yet'}</dd>
            {l.verified && (<><dt>Verified</dt><dd>by {l.verified.verified_by_name || 'a VA'} on {longDate(l.verified.verified_at)}: {l.verified.verified_note}</dd></>)}
            {l.episode && l.episode.verdict !== 'NEW_LEAD_OK' && (<><dt>Episode</dt><dd>{l.episode.line} — not a new lead. If the provider saw them again for a replacement, record it on Replacements.</dd></>)}
            {holds.length > 0 && (<><dt>Holds</dt><dd>Held from the PA's queue until fixed: {holds.map(reasonWords).join('; ')}</dd></>)}
            {l.workable === false && (<><dt>Finished case</dt><dd>{finishedWords(l)}</dd></>)}
            <dt>Open copies</dt><dd>{l.open_cases}</dd>
          </dl>
        </div>
      ),
    });
    return out;
  };

  let state: React.ReactNode = null;
  if (!leads) state = <MfSkeleton bare />;
  else if (leads.length === 0) state = <TideState kind="empty" reason="none" kindLabel="Nothing waiting" message="No lead is waiting to be placed." />;
  else if (shown.length === 0) {
    state = (
      <div className="mf-panel">
        <p>No lead matches this search or filter. {leads.length} lead{leads.length === 1 ? '' : 's'} remain.</p>
        <div className="mf-acts">
          <button type="button" className="board-btn" onClick={() => { setFilter(''); setLegendKey(null); }}>Clear search and filter</button>
        </div>
      </div>
    );
  }

  const controls = leads && leads.length > 0 ? (
    <>
      <input type="search" className="mf-filter" aria-label="Find a resident, facility or PA" placeholder="Find a resident, facility or PA" value={filter} onChange={(e) => setFilter(e.target.value)} />
      {can && (
        <>
          <label className="mf-check">
            <input type="checkbox" checked={allShown} onChange={() => select(shownIds, !allShown)} aria-label={`Select all ${shownIds.length} shown`} />
            Select all {shownIds.length} shown
          </label>
          {selected.size > 0 && hiddenSelected > 0 && <span className="mf-sub">{selected.size} selected, {hiddenSelected} not shown by the filter</span>}
          <BulkDisposition count={selected.size} resolveIds={async () => Array.from(selected)} labelFor={labelFor}
            onClear={() => setSelected(new Set())}
            onFinished={async (m) => { setNotice(m); setSelected(new Set()); await load(); }} />
        </>
      )}
    </>
  ) : null;

  return (
    <main className="mf-main mf-main--board">
      <MfBoard<DeskLead>
        title="Leads to place"
        count={leads ? leads.length : null}
        units={['lead', 'leads']}
        what="One row per resident nobody holds, buildings A to Z. Open a row to place that resident, place their whole building, or close them instead. Nothing is distributed automatically."
        notice={<>
          {notice && <p className="mf-notice" role="status">{notice}</p>}
          {!can && leads && <p className="mf-notice">You can see these leads; placing them needs the lead-placement permission.</p>}
        </>}
        controls={controls}
        legend={leads && leads.length > 0 ? (
          <MfLegend title="Where leads come from, and who can be placed" items={legendItems} whole={found.length} wholeWords="in this list" units={['lead', 'leads']}
            value={legendKey} onSelect={setLegendKey}
            scope="Source chips say where a lead came from; a lead can carry several. The last three say whether it can be placed. Press an entry to show only those leads." />
        ) : null}
        state={state}
        columns={columns}
        identity={identity}
        rows={shown}
        rowKey={(l) => l.case_id}
        openKey={open}
        onOpenChange={setOpen}
        detailSections={sections}
        detailLabel={(l) => `${residentLabel(l)}: the lead`}
        sort={sort} dir={dir} sorts={sorts} onSort={onSort}
      />
    </main>
  );
}

type CoverageBasis = 'confirmed' | 'suggested' | 'none';
const basisOf = (r: Coverage): CoverageBasis => (r.source === 'HISTORY' && !r.confirmed_at ? 'suggested' : r.advocate_user_id ? 'confirmed' : 'none');

function BasisChip({ r }: { r: Coverage }) {
  const b = basisOf(r);
  if (b === 'suggested') return <ToneChip tone="attn">Suggested from history, {Math.round(Number(r.history_share))}%</ToneChip>;
  if (b === 'confirmed') return <ToneChip tone="ok">Confirmed</ToneChip>;
  return <ToneChip tone="quiet">Nobody set</ToneChip>;
}

const COVERAGE_SORTERS: Record<string, (r: Coverage) => string | number | null> = {
  facility: (r) => r.facility_name,
  open: (r) => r.open_cases,
  who: (r) => r.advocate,
};

/** The coverage edit: who covers the building and why. Same call, same enablement as always. */
function CoveragePanel({ row, advocates, onSaved }: { row: Coverage; advocates: Advocate[]; onSaved: (msg: string) => void }) {
  const [who, setWho] = useState(row.advocate_user_id || '');
  const [reason, setReason] = useState(row.reason || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const changed = who !== (row.advocate_user_id || '') || (reason || '') !== (row.reason || '');
  const unconfirmed = row.source === 'HISTORY' && !row.confirmed_at;
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await manifestApi.setCoverage(row.facility_id, who || null, reason || null);
      if (r.success) onSaved(`Coverage for ${row.facility_name} saved.`); else setErr(r.message || r.error || 'Could not save.');
    } catch { setErr('Could not reach eMerge. Try again.'); } finally { setBusy(false); }
  };
  return (
    <div className="mf-panel">
      <p>{row.open_cases} open {row.open_cases === 1 ? 'case' : 'cases'}.{unconfirmed ? ` Suggested from history (${Math.round(Number(row.history_share))}% of recent placements), not yet confirmed.` : ''}</p>
      <div className="mf-acts">
        <select aria-label={`PA covering ${row.facility_name}`} value={who} onChange={(e) => setWho(e.target.value)} disabled={busy}>
          <option value="">Nobody set</option>
          {advocates.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <input aria-label={`Why, for ${row.facility_name}`} placeholder="Why (e.g. Spanish-speaking)" value={reason} maxLength={200}
          onChange={(e) => setReason(e.target.value)} disabled={busy} />
        <button type="button" className={`board-btn${changed || unconfirmed ? ' board-btn--primary' : ''}`} onClick={save} disabled={busy || (!changed && !unconfirmed)}>
          {busy ? 'Saving…' : unconfirmed && !changed ? 'Confirm' : 'Save'}
        </button>
      </div>
      {err && <p className="mf-notice mf-notice--stop" role="alert">{err}</p>}
    </div>
  );
}

function CoverageBoard() {
  const [coverage, setCoverage] = useState<Coverage[] | null>(null);
  const [advocates, setAdvocates] = useState<Advocate[]>([]);
  const [can, setCan] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [legendKey, setLegendKey] = useState<string | null>(null);
  const [open, setOpen] = useOpenParam('open');

  const load = useCallback(async () => {
    setProblem(null);
    try {
      const r = await manifestApi.coverage();
      if (r.success && r.data) { setCoverage(r.data.items); setAdvocates(r.data.advocates); setCan(r.data.canEdit); }
      else setProblem(r.message || r.error || 'Could not load facility coverage.');
    } catch { setProblem('Could not reach eMerge.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const found = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const rows = coverage || [];
    return q ? rows.filter((r) => r.facility_name.toLowerCase().includes(q) || (r.advocate || '').toLowerCase().includes(q)) : rows;
  }, [coverage, filter]);
  const filtered = useMemo(() => (legendKey ? found.filter((r) => basisOf(r) === legendKey) : found), [found, legendKey]);
  const { rows: shown, sort, dir, sorts, onSort } = useClientSort(filtered, COVERAGE_SORTERS);

  const legendItems: MfLegendItem[] = useMemo(() => {
    const meaning: Record<CoverageBasis, string> = {
      confirmed: 'Somebody set or confirmed this PA for the building.',
      suggested: 'Seeded from who recently received this building\'s placements; nobody has confirmed it yet.',
      none: 'No PA covers this building; a lead there suggests nobody.',
    };
    const label: Record<CoverageBasis, string> = { confirmed: 'Confirmed', suggested: 'Suggested from history', none: 'Nobody set' };
    const out: MfLegendItem[] = [];
    for (const b of ['confirmed', 'suggested', 'none'] as CoverageBasis[]) {
      const hits = found.filter((r) => basisOf(r) === b);
      if (hits.length) out.push({ key: b, label: label[b], sample: <BasisChip r={hits[0]} />, meaning: meaning[b], count: hits.length });
    }
    return out;
  }, [found]);

  if (problem) return <main className="mf-main mf-main--board"><MfLoadFailure message={problem} retry={load} /></main>;

  const columns: BoardColumn<Coverage>[] = [
    { key: 'open', label: 'Open cases', numeric: true, sort: 'open', help: 'Open cases in the building.', render: (r) => <span>{r.open_cases}</span> },
    { key: 'who', label: 'Covered by', sort: 'who', help: 'The PA a lead from this building suggests. The placer can always choose someone else.', render: (r) => (r.advocate ? <span className="board-clip" title={r.advocate}>{r.advocate}</span> : <span className="board-na">nobody</span>) },
    { key: 'basis', label: 'Basis', help: 'Confirmed: somebody set it. Suggested from history: seeded from recent placements, share shown. Nobody set.', render: (r) => <BasisChip r={r} /> },
    { key: 'why', label: 'Why', help: 'The reason the PA covers the building, as the placer wrote it.', render: (r) => (r.reason ? <span className="board-clip" title={r.reason}>{r.reason}</span> : <span className="board-na">not recorded</span>) },
  ];
  const identity = {
    label: 'Facility', sort: 'facility',
    help: 'A building. Open the row to set which PA covers it and why.',
    render: (r: Coverage) => <strong>{r.facility_name}</strong>,
    name: (r: Coverage) => r.facility_name,
  };
  const sections = (r: Coverage): BoardDetailSection[] => (can
    ? [{ id: 'coverage', label: 'Coverage', render: () => <CoveragePanel key={r.facility_id} row={r} advocates={advocates.filter((a) => !a.manager)} onSaved={(m) => { setNotice(m); load(); }} /> }]
    : [{
      id: 'coverage', label: 'Coverage',
      render: () => (
        <div className="mf-panel">
          <p>{r.advocate ? `Covered by ${r.advocate}.` : 'Nobody is set for this building.'} Changing coverage needs the lead-placement permission.</p>
        </div>
      ),
    }]);

  const covered = (coverage || []).filter((r) => r.advocate_user_id).length;
  let state: React.ReactNode = null;
  if (!coverage) state = <MfSkeleton bare />;
  else if (coverage.length === 0) state = <TideState kind="empty" reason="none" message="No facility is listed." />;
  else if (shown.length === 0) {
    state = (
      <div className="mf-panel">
        <p>No facility matches this search or filter.</p>
        <div className="mf-acts"><button type="button" className="board-btn" onClick={() => { setFilter(''); setLegendKey(null); }}>Clear search and filter</button></div>
      </div>
    );
  }

  return (
    <main className="mf-main mf-main--board">
      <MfBoard<Coverage>
        title="Facility coverage"
        count={coverage ? coverage.length : null}
        units={['facility', 'facilities']}
        what={`Which PA covers each facility${coverage ? ` (${covered} of ${coverage.length} covered)` : ''}. It is what a lead suggests; the placer can always choose someone else. Open a row to change it.`}
        notice={notice ? <p className="mf-notice" role="status">{notice}</p> : null}
        controls={coverage && coverage.length > 0 ? (
          <input type="search" className="mf-filter" aria-label="Find a facility or PA" placeholder="Find a facility or PA" value={filter} onChange={(e) => setFilter(e.target.value)} />
        ) : null}
        legend={coverage && coverage.length > 0 ? (
          <MfLegend title="How each building's PA was set" items={legendItems} whole={found.length} wholeWords="in this list" units={['facility', 'facilities']} exclusive
            value={legendKey} onSelect={setLegendKey}
            scope="Every facility is in exactly one of these. Press an entry to show only those facilities." />
        ) : null}
        state={state}
        columns={columns}
        identity={identity}
        rows={shown}
        rowKey={(r) => r.facility_id}
        openKey={open}
        onOpenChange={setOpen}
        detailSections={sections}
        detailLabel={(r) => `${r.facility_name}: coverage`}
        sort={sort} dir={dir} sorts={sorts} onSort={onSort}
      />
    </main>
  );
}

export default function LeadDesk({ view }: { view: 'leads' | 'coverage' }) {
  return view === 'leads' ? <LeadsBoard /> : <CoverageBoard />;
}
